import React from 'react';
import { getSession } from 'next-auth/react';
import type { NextPage, GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next';

interface WithSSRAuthOptions {
  role?: string;
  type?: string;
}

/**
 * Returns a getServerSideProps function that enforces authentication (and optional role/type)
 * and delegates to an optional original getServerSideProps.
 */
export function getServerSideAuthProps<P extends Record<string, any>>(
  getServerSidePropsFunc?: GetServerSideProps<P>,
  options?: WithSSRAuthOptions,
): GetServerSideProps<P> {
  const { role: requiredRole, type: requiredType } = options ?? {};

  return async (
    ctx: GetServerSidePropsContext,
  ): Promise<GetServerSidePropsResult<P>> => {
    const session = await getSession(ctx);
    if (!session) {
      return {
        redirect: {
          destination: '/login',
          permanent: false,
        },
      };
    }

    if (requiredRole && session.user.role !== requiredRole) {
      return {
        redirect: {
          destination: '/unauthorized',
          permanent: false,
        },
      };
    }

    if (requiredType && session.user.type !== requiredType) {
      return {
        redirect: {
          destination: '/unauthorized',
          permanent: false,
        },
      };
    }

    let pageProps: Record<string, unknown> = {};
    if (getServerSidePropsFunc) {
      const result = await getServerSidePropsFunc(ctx);
      if ('props' in result) {
        pageProps = result.props;
      } else {
        return result;
      }
    }

    return {
      props: {
        session,
        ...pageProps,
      },
    };
  };
}

/**
 * Higher-order component to wrap pages that require authentication (and optional role/type)
 * using Next.js getServerSideProps for page-level protection.
 */
export function withSSRAuth<P extends Record<string, any>>(
  WrappedComponent: NextPage<P>,
  options?: WithSSRAuthOptions,
): NextPage<P> {
  const { role: requiredRole, type: requiredType } = options ?? {};

  const ComponentWithAuth: NextPage<P> = (props) => <WrappedComponent {...props} />;

  return ComponentWithAuth;
}